import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { CalendarDays } from "lucide-react";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import Pagination from "../../GlobalManagement/Pages/Pagination";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect
} from '../../GlobalManagement/Pages/masterPageStyles';

const pad = (n) => String(n).padStart(2, "0");

const toDDMMYYYY = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  }
  return raw;
};

const toDDMMYYYYSlash = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  return raw;
};

const normalizeCode = (value) => String(value || "").trim().toUpperCase();

const parseDDCodes = (value) => {
  const code = normalizeCode(value);
  if (code.startsWith("DD:")) {
    const payload = code.slice(3);
    if (payload.includes("+")) {
      const [first = "", second = ""] = payload.split("+");
      return [normalizeCode(first), normalizeCode(second || first)];
    }
    if (payload.length === 2) return [payload[0], payload[1]];
    return [normalizeCode(payload), normalizeCode(payload)];
  }
  if (/^[A-Z]{2}$/.test(code) && !["OFF", "DD"].includes(code)) {
    return [code[0], code[1]];
  }
  return null;
};

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const DutyRoasterHistory = () => {
  const PER_PAGE_STORAGE_KEY = "dutyRoasterHistory.perPage";
  const DATE_STORAGE_KEY = "dutyRoasterHistory.selectedDate";
  const SHIFT_STORAGE_KEY = "dutyRoasterHistory.selectedShift";

  const getStoredPerPage = () => {
    const raw = localStorage.getItem(PER_PAGE_STORAGE_KEY);
    if (!raw) return 20;
    if (raw === "all") return "all";
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : 20;
  };

  const getStoredDate = () => {
    const raw = localStorage.getItem(DATE_STORAGE_KEY);
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  };
  const getStoredShift = () => localStorage.getItem(SHIFT_STORAGE_KEY) || "ALL";

  const [selectedDate, setSelectedDate] = useState(getStoredDate);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState(["ALL"]);
  const [designations, setDesignations] = useState(["ALL"]);
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [selectedDesignation, setSelectedDesignation] = useState("ALL");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");
  const [selectedShift, setSelectedShift] = useState(getStoredShift);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(getStoredPerPage);
  const [rows, setRows] = useState([]);
  const [shiftMap, setShiftMap] = useState({});
  const dateInputRef = useRef(null);

  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  useEffect(() => {
    const fetchBaseData = async () => {
      const [empRes, deptRes, shiftRes] = await Promise.all([
        axios.get("/api/employees"),
        axios.get("/api/departments"),
        axios.get("/api/shifts"),
      ]);

      const empList = empRes.data || [];
      setEmployees(empList);
      setDepartments(["ALL", ...(deptRes.data || []).map((d) => d.deptName)]);

      const codeMap = {};
      (shiftRes.data || []).forEach((s) => {
        const code = normalizeCode(s.shiftCode);
        if (!code) return;
        codeMap[code] = {
          code,
          shiftName: s.shiftName || "-",
          startTime: s.startTime || "-",
          endTime: s.endTime || "-",
        };
      });
      setShiftMap(codeMap);
    };

    fetchBaseData();
  }, []);

  useEffect(() => {
    let scopedEmployees = employees;
    if (selectedDepartment !== "ALL") {
      scopedEmployees = employees.filter(
        (emp) => emp.departmentName === selectedDepartment
      );
    }
    setDesignations([
      "ALL",
      ...new Set(scopedEmployees.map((emp) => emp.designationName).filter(Boolean)),
    ]);
  }, [employees, selectedDepartment]);

  const employeePrefixOptions = useMemo(() => {
    const prefixes = employees
      .map((emp) => getEmployeePrefix(emp.employeeID))
      .filter(Boolean);
    return ["ALL", ...new Set(prefixes)];
  }, [employees]);

  useEffect(() => {
    const fetchDutyRows = async () => {
      if (!selectedDate || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
        setRows([]);
        return;
      }

      const dateObj = new Date(selectedDate);
      const monthStr = dateObj.toLocaleString("en-US", { month: "short" });
      const year = dateObj.getFullYear();
      const day = dateObj.getDate();
      const roasterMonth = `${monthStr}-${year}`;

      const res = await axios.get(`/api/shift-management/${roasterMonth}`);
      const rosterMap = {};
      (res.data || []).forEach((doc) => {
        rosterMap[doc.employeeUserId] = doc;
      });

      const computedRows = (employees || []).map((emp) => {
        if (isExEmployeeId(emp.employeeID)) return null;

        const doc = rosterMap[emp.employeeUserId];
        const rawShiftCode = normalizeCode(
          (doc && doc.shifts && (doc.shifts[day] || doc.shifts[String(day)])) || "-"
        );

        let shiftCode = rawShiftCode || "-";
        let shiftName = "-";
        let startTime = "-";
        let endTime = "-";

        if (shiftCode === "OFF") {
          shiftName = "OFF";
        } else {
          const ddParts = parseDDCodes(shiftCode);
          if (ddParts) {
            const [firstCode, secondCode] = ddParts;
            const first = shiftMap[firstCode];
            const second = shiftMap[secondCode];
            if (first || second) {
              const n1 = first?.shiftName || firstCode || "-";
              const n2 = second?.shiftName || secondCode || n1;
              shiftName = `${n1} + ${n2}`;
              startTime = first?.startTime || "-";
              endTime = second?.endTime || first?.endTime || "-";
              shiftCode = `DD:${firstCode}+${secondCode || firstCode}`;
            }
          } else if (shiftMap[shiftCode]) {
            shiftName = shiftMap[shiftCode].shiftName || "-";
            startTime = shiftMap[shiftCode].startTime || "-";
            endTime = shiftMap[shiftCode].endTime || "-";
          }
        }

        return {
          date: toDDMMYYYY(selectedDate),
          employeeId: emp.employeeID || "-",
          employeeUserId: emp.employeeUserId || "-",
          employeeName: `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "-",
          departmentName: emp.departmentName || "-",
          designationName: emp.designationName || "-",
          shiftCode,
          shiftName,
          shiftStartTime: startTime,
          shiftEndTime: endTime,
        };
      }).filter(Boolean);

      setRows(computedRows);
    };

    fetchDutyRows();
  }, [selectedDate, employees, shiftMap]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, selectedDepartment, selectedDesignation, selectedEmployeePrefix, selectedShift, searchTerm, perPage]);

  useEffect(() => {
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(perPage));
  }, [perPage]);

  useEffect(() => {
    localStorage.setItem(DATE_STORAGE_KEY, selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    localStorage.setItem(SHIFT_STORAGE_KEY, selectedShift);
  }, [selectedShift]);

  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  const shiftOptions = useMemo(() => {
    const codes = Object.keys(shiftMap)
      .filter((c) => c && c !== "OFF")
      .sort();
    return ["ALL", "OFF", "DD", "---", ...codes];
  }, [shiftMap]);

  const filteredRows = useMemo(() => {
    const q = String(searchTerm || "").toUpperCase().trim();
    const selectedShiftCode = normalizeCode(selectedShift);
    return rows.filter((row) => {
      const rowShiftCode = normalizeCode(row.shiftCode);
      const departmentMatch =
        selectedDepartment === "ALL" || row.departmentName === selectedDepartment;
      const designationMatch =
        selectedDesignation === "ALL" || row.designationName === selectedDesignation;
      const prefixMatch =
        selectedEmployeePrefix === "ALL" ||
        getEmployeePrefix(row.employeeId) === selectedEmployeePrefix;
      const shiftMatch =
        selectedShiftCode === "ALL"
          ? true
          : selectedShiftCode === "OFF"
          ? rowShiftCode === "OFF"
          : selectedShiftCode === "DD"
          ? rowShiftCode.startsWith("DD:")
          : selectedShiftCode === "---"
          ? !rowShiftCode || rowShiftCode === "-"
          : rowShiftCode === selectedShiftCode ||
            (rowShiftCode.startsWith("DD:") &&
              parseDDCodes(rowShiftCode)?.includes(selectedShiftCode));
      if (!departmentMatch || !designationMatch || !prefixMatch || !shiftMatch) return false;
      if (!q) return true;
      const name = String(row.employeeName || "").toUpperCase();
      const userId = String(row.employeeUserId || "").toUpperCase();
      const employeeId = String(row.employeeId || "").toUpperCase();
      return name.includes(q) || userId.includes(q) || employeeId.includes(q);
    });
  }, [rows, selectedDepartment, selectedDesignation, selectedEmployeePrefix, selectedShift, searchTerm]);

  const startIndex = perPage === "all" ? 0 : (currentPage - 1) * perPage;
  const paginatedRows =
    perPage === "all"
      ? filteredRows
      : filteredRows.slice(startIndex, startIndex + perPage);

  const handleExportExcel = () => {
    const exportData = filteredRows.map((row, index) => ({
      "SL": index + 1,
      "Date": row.date,
      "Employee ID": row.employeeId,
      "User ID": row.employeeUserId,
      "Name": row.employeeName,
      "Department": row.departmentName,
      "Designation": row.designationName,
      "Shift Code": row.shiftCode,
      "Shift Name": row.shiftName,
      "Start Time": row.shiftStartTime,
      "End Time": row.shiftEndTime,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Duty Roaster History");
    XLSX.writeFile(workbook, `Duty_Roaster_History_${toDDMMYYYY(selectedDate)}.xlsx`);
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><CalendarDays size={14} /></div>
              <div><h1 className={masterTitle}>Duty Roaster History</h1></div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleExportExcel}
                className={masterPrimaryButton}
              >
                EXPORT EXCEL
              </button>
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex flex-col flex-1 min-h-0`}>
          <MobileHeaderToggle>
            {/* Filter and Controls Section */}
            <div className={`${masterPanel} p-3 mb-2 flex flex-col gap-3`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Date</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={toDDMMYYYYSlash(selectedDate)}
                      readOnly
                      placeholder="dd/mm/yyyy"
                      className={masterInput + " pr-10"}
                      onClick={openDatePicker}
                    />
                    <button
                      type="button"
                      onClick={openDatePicker}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-600"
                      aria-label="Open calendar"
                    >
                      <CalendarDays size={16} />
                    </button>
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      tabIndex={-1}
                      className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                    />
                  </div>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Department</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setSelectedDesignation("ALL");
                    }}
                    className={masterSelect}
                  >
                    {departments.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Designation</label>
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    className={masterSelect}
                  >
                    {designations.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    value={selectedEmployeePrefix}
                    onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                    className={masterSelect}
                  >
                    {employeePrefixOptions.map((prefix) => (
                      <option key={prefix} value={prefix}>
                        {prefix}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Shift</label>
                  <select
                    value={selectedShift}
                    onChange={(e) => setSelectedShift(normalizeCode(e.target.value) || "ALL")}
                    className={masterSelect}
                  >
                    {shiftOptions.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(normalizeSearchInput(val));
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by name, user ID, or employee ID..." 
                  totalItems={filteredRows.length} 
                  currentPage={currentPage} 
                  pageSize={perPage} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={(newSize) => {
                    setPerPage(newSize);
                    setCurrentPage(1);
                  }} 
                />
              </div>
            </div>
          </MobileHeaderToggle>

          {/* Table Container with Mobile Touch Support */}
          <div 
            className={`${masterPanel} w-full flex-1 min-h-0 overflow-x-auto overflow-y-auto scroll-smooth touch-pan-x`}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <table className={`${masterTable} min-w-[1200px]`}>
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={masterTableData} style={{ width: '50px' }}>SL</th>
                  <th className={masterTableData}>Date</th>
                  <th className={masterTableData}>Employee ID</th>
                  <th className={masterTableData}>User ID</th>
                  <th className={masterTableData}>Name</th>
                  <th className={masterTableData}>Department</th>
                  <th className={masterTableData}>Designation</th>
                  <th className={masterTableData}>Shift Code</th>
                  <th className={masterTableData}>Shift Name</th>
                  <th className={masterTableData}>Start Time</th>
                  <th className={masterTableData}>End Time</th>
                </tr>
              </thead>
              <tbody className={masterTbody}>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, idx) => (
                    <tr
                      key={`${row.employeeUserId}-${idx}`}
                      className={`${masterTableRow} ${idx % 2 === 0 ? "bg-slate-50/50" : "bg-white"}`}
                    >
                      <td className={masterTableCell + " text-center"}>
                        {perPage === "all" ? idx + 1 : startIndex + idx + 1}
                      </td>
                      <td className={masterTableCell}>{row.date}</td>
                      <td className={masterTableCell}>{row.employeeId}</td>
                      <td className={masterTableCell}>{row.employeeUserId}</td>
                      <td className={masterTableCell + " uppercase"}>{row.employeeName}</td>
                      <td className={masterTableCell}>{row.departmentName}</td>
                      <td className={masterTableCell}>{row.designationName}</td>
                      <td className={masterTableCell}>{row.shiftCode}</td>
                      <td className={masterTableCell}>{row.shiftName}</td>
                      <td className={masterTableCell}>{row.shiftStartTime}</td>
                      <td className={masterTableCell}>{row.shiftEndTime}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-6 text-slate-400 font-semibold uppercase text-xs">
                      No duty roaster history data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

      
        </main>
      </div>
    </div>
  );
};

export default DutyRoasterHistory;